import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw, Activity } from "lucide-react";

const ICON = {
  ok: { Icon: CheckCircle2, cls: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  warn: { Icon: AlertTriangle, cls: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  error: { Icon: XCircle, cls: "text-red-600", bg: "bg-red-50 border-red-200" },
  info: { Icon: Info, cls: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
};

function Row({ check }) {
  const { Icon, cls, bg } = ICON[check.status] || ICON.info;
  return (
    <div data-testid={`health-${check.key}`} className={`flex items-center gap-4 border p-4 rounded-sm ${bg}`}>
      <Icon className={`w-6 h-6 shrink-0 ${cls}`} />
      <div className="flex-1">
        <div className="font-semibold text-[#0A192F]">{check.label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{check.detail}</div>
      </div>
      <span data-testid={`health-status-${check.key}`} className={`text-[10px] font-semibold uppercase tracking-wider ${cls}`}>{check.status}</span>
    </div>
  );
}

export default function AdminHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Frontend-configured integrations (GA4 + Clarity live in the browser, not the API).
  const analyticsEnabled = String(process.env.REACT_APP_ANALYTICS_ENABLED || "").toLowerCase() === "true";
  const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
  const clarityId = process.env.REACT_APP_CLARITY_PROJECT_ID || "";

  const gaCheck = () => {
    if (!analyticsEnabled || !gaId) return { key: "ga", label: "Google Analytics", status: "warn", detail: "Not configured" };
    const live = typeof window !== "undefined" && typeof window.gtag === "function";
    return { key: "ga", label: "Google Analytics", status: live ? "ok" : "warn", detail: live ? `Active · ${gaId}` : `Configured (${gaId}) — not loaded yet` };
  };
  const clarityCheck = () => {
    if (!analyticsEnabled || !clarityId) return { key: "clarity", label: "Microsoft Clarity", status: "warn", detail: "Not configured" };
    const live = typeof window !== "undefined" && typeof window.clarity === "function";
    return { key: "clarity", label: "Microsoft Clarity", status: live ? "ok" : "warn", detail: live ? `Active · ${clarityId}` : `Configured (${clarityId}) — not loaded yet` };
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/health");
      setData(data);
    } catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Failed to load health"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const serverChecks = data?.checks || [];
  // Insert GA + Clarity right after the backend checks for a full picture.
  const allChecks = [...serverChecks, gaCheck(), clarityCheck()];
  const overall = (() => {
    if (allChecks.some((c) => c.status === "error")) return "error";
    if (allChecks.some((c) => c.status === "warn")) return "warn";
    return "ok";
  })();
  const OverallIcon = (ICON[overall] || ICON.info).Icon;

  return (
    <div className="space-y-8" data-testid="admin-health-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline text-[#E63946] flex items-center gap-2"><Activity className="w-4 h-4" /> System Status</div>
          <h1 className="font-display text-4xl uppercase tracking-tighter mt-1">Health Dashboard</h1>
          <p className="text-sm text-slate-600 mt-2">Live status of every critical integration and service.</p>
        </div>
        <button data-testid="health-refresh" onClick={load} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {!loading && (
        <div data-testid="health-overall" className={`flex items-center gap-3 p-4 rounded-sm border ${(ICON[overall] || ICON.info).bg}`}>
          <OverallIcon className={`w-7 h-7 ${(ICON[overall] || ICON.info).cls}`} />
          <div>
            <div className="font-display text-xl uppercase tracking-tighter text-[#0A192F]">
              {overall === "ok" ? "All Systems Operational" : overall === "warn" ? "Attention Needed" : "Action Required"}
            </div>
            {data?.generated_at && <div className="text-xs text-slate-500">Checked {new Date(data.generated_at).toLocaleString()}</div>}
          </div>
        </div>
      )}

      {error && <div data-testid="health-error" className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{error}</div>}

      {loading ? (
        <div className="text-slate-400 text-sm">Running checks…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {allChecks.map((c) => <Row key={c.key} check={c} />)}
        </div>
      )}
    </div>
  );
}
